import Head from 'next/head';

import { HOMEPAGE_URL } from '../../../helpers/globals';
import type { ListingCardData } from '../property/SimilarListings';

interface DirectorySeoHeadProps {
  canonicalUrl: string;
  listings: ListingCardData[];
  locationName: string;
  seoDescription: string;
  seoTitle: string;
  totalCount: number;
}

export const DirectorySeoHead: React.FC<DirectorySeoHeadProps> = ({
  canonicalUrl,
  listings,
  locationName,
  seoDescription,
  seoTitle,
  totalCount,
}) => {
  const firstListingImage = listings[0]?.photos?.[0];

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: listings.slice(0, 10).map((listing, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      item: {
        '@type': 'Product',
        name: listing.fullAddress,
        url: `${HOMEPAGE_URL}/properties/${listing.slug}/aid_${listing.addressToken}`,
        offers: {
          '@type': 'Offer',
          price: listing.listPrice,
          priceCurrency: 'USD',
        },
      },
    })),
  };

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Homes', item: `${HOMEPAGE_URL}/homes` },
  ];

  breadcrumbItems.push({
    '@type': 'ListItem',
    position: 2,
    name: locationName,
    item: canonicalUrl,
  });

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  return (
    <Head>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      {firstListingImage && <meta property="og:image" content={firstListingImage} />}

      {/* Schema: ItemList for listings */}
      {listings.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }}
        />
      )}

      {/* Schema: BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      {/* Robots: noindex if empty */}
      {totalCount === 0 && <meta name="robots" content="noindex" />}
    </Head>
  );
};
